import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { useHeaderContext } from "../../../context/headerContext";
import { useUserContext } from "../../../context/userContext";
import Button from "../../../components/Button";
import ContentCard from "../../../components/ContentCard";
import { parseISO, isAfter, format } from 'date-fns';
import fetchWithRefresh from "../../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../utils/LogErrors";

export default function Venue() {
  const { setTitle, setSearchFunc,setBackButton } = useHeaderContext();
  const [venueData, setVenueData] = useState(null);
  const [venueDataPoints, setVenueDataPoints] = useState(null);
  const { state } = useLocation();
  const { venue } = state;
  const { user, logout, updateTokens } = useUserContext();

  useEffect(() => {
    setTitle(venue?.OrgPartnerFriendlyName);
    setSearchFunc(null);
    setBackButton(true);
    fetchData();
    fetchVenueDataPoints();
  }, []);

  const fetchVenueDataPoints = async () => {
    try {
      return await Sentry.startSpan({ name: "Fetch Venue Data Points", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}CollectableDataPoints/FetchCollectableDataPointsForVenue?OrgID=${venue.OrgID}`;
        span.setAttribute("http.url", url);
        span.setAttribute("venueId", venue.OrgID);
  
        const response = await fetchWithRefresh(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        if (response.ok) {
          const data = await response.json();
          setVenueDataPoints(data);
          span.setStatus("ok");
        } else {
          const errorMessage = `Failed to fetch venue data points. Status: ${response.status}`;
          span.setStatus("error");
          Sentry.captureMessage(errorMessage, "warning");
          throw new Error(errorMessage);
        }
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { venueId: venue.OrgID } });
      LogErrors(user, logout, updateTokens, "Error fetching venue data points: " + error);
    }
  };

  const fetchData = async () => {
    try {
      return await Sentry.startSpan({ name: "Fetch Venue Data", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}CollectableDataPoints/FetchVenueDataPointHistory?OrgID=${venue.OrgID}&pagesize=10`;
        span.setAttribute("http.url", url);
        span.setAttribute("venueId", venue.OrgID);
  
        const response = await fetchWithRefresh(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        if (response.ok) {
          const data = await response.json();
          setVenueData(data);
          span.setStatus("ok");
        } else {
          const errorMessage = `Failed to fetch venue data. Status: ${response.status}`;
          span.setStatus("error");
          Sentry.captureMessage(errorMessage, "warning");
          throw new Error(errorMessage);
        }
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { venueId: venue.OrgID } });
      LogErrors(user, logout, updateTokens, "Error fetching venue data: " + error);
    }
  };  

  if (!venueData || !venueDataPoints) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-150px)]">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-y-6">
        <div className="relative w-full h-[197px]">
          <img
            src={venue.DirectoryDisplayImage}
            alt={venue.OrgPartnerFriendlyName}
            className="w-full h-full object-cover rounded-[4px] border-[1px] border-solid border-[#E3E3E3]"
          />
          {venue.MembershipsAvailable === 1 && (
            <span className="select-none uppercase pl-[11.8px] pr-[9.5px] bg-white rounded-[4px] text-[12px] leading-[24px] tracking-[2.4px] font-semibold top-2 right-2 absolute">
              Memberships Available
            </span>
          )}
        </div>
        <div className="flex flex-col">
          <h3>{venue.DirectoryDisplayName}</h3>
          <p>{venue.VenueAddress}</p>
        </div>
        {venue?.BookingAvailable === 1 && (
          <div className="flex flex-col justify-stretch border-b border-dashed border-[#E3E3E3] pb-[24px]">
            <Link className="w-full" to={venue.OrgWebsiteURL} target="_blank">
              <Button className="w-full">Book Now</Button>
            </Link>
          </div>
        )} 
        <div>
          {venueData?.datapoints?.length > 0 && (
            <>
              <p className="select-none uppercase bg-white rounded-[4px] text-[12px] leading-[24px] tracking-[2.4px] font-semibold mb-[12px]">
                Current Data
              </p>
              <div className="grid grid-cols-2 gap-[18px] mb-[24px]">
                {venueData?.datapoints[0]?.dataPointValues?.map((dataPoint, index) => {
                  return (
                    <ContentCard
                      icon={dataPoint?.dataPointIcon}
                      title={dataPoint?.dataPointPrefix + dataPoint?.dataPointValue + dataPoint?.dataPointSuffix}
                      desc={dataPoint?.dataPointType}
                      type="weather"
                      key={index}
                    />
                  )
                })}
              </div>
            </>
          )}
          
          <div className="flex flex-col justify-stretch border-b border-dashed border-[#E3E3E3] pb-[24px]">
            <Link className="w-full" to={venue.OrgWebsiteURL} target="_blank">
              <Button className="w-full font-semibold" variant="tertiary">
                Visit Website
              </Button>
            </Link>
          </div>
        </div>
        <div>
          <p className="select-none uppercase bg-white rounded-[4px] text-[12px] leading-[24px] tracking-[2.4px] font-semibold mb-[12px]">
            About
          </p>
          <p className="text-[#727272]">{venue.VenueHTMLBlurb}</p>
        </div>
      </div>
    </>
  );
}
